import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useWheelHistory } from '../hooks/useLocalStorage';
import useConfetti from '../hooks/useConfetti';
import SpinWheel from './SpinWheel';
import EntryList from './EntryList';
import ResultModal from './ResultModal';
import SEOHead from './SEOHead';
import SettingsPanel from './SettingsPanel';
import HistoryPanel from './HistoryPanel';
import Leaderboard from './Leaderboard';
import SaveLoadWheel from './SaveLoadWheel';
import QRCodeShare from './QRCodeShare';
import SpinStatistics from './SpinStatistics';
import EmbedCode from './EmbedCode';
import TeamMode from './TeamMode';
import TournamentMode from './TournamentMode';
import CountdownOverlay from './CountdownOverlay';
import SEOContentSections from './SEOContentSections';
// TEMPORARILY DISABLED - Debugging production white screen
// import ViralShareButtons from './ViralShareButtons';
import { Settings, History, Share2, Maximize2, X, Shield, ChevronDown, ChevronUp, Trophy, Download } from 'lucide-react';
import { getThemeColors, getTheme } from '../config/wheelThemes';
import { getSlugForLanguage } from '../App';
// TEMPORARILY DISABLED - Debugging production white screen
// import { getSEOMeta } from '../config/seoMetaTemplates';
// import { generateEnhancedSchema } from '../utils/enhancedSchema';

// Helper function to smooth scroll to element
const scrollToElement = (element, offset = 100) => {
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition - offset,
      behavior: 'smooth'
    });
  }
};

// Default presets (fallback if translations not available)
const DEFAULT_PRESETS = {
  main: ['Option 1', 'Option 2', 'Option 3', 'Option 4', 'Option 5', 'Option 6'],
  yesNo: ['Yes', 'No', 'Maybe'],
  namePicker: ['Alice', 'Bob', 'Charlie', 'Diana', 'Emma', 'Frank', 'Grace', 'Henry'],
  teamGenerator: ['Player 1', 'Player 2', 'Player 3', 'Player 4', 'Player 5', 'Player 6'],
  numberWheel: Array.from({length: 10}, (_, i) => String(i + 1)),
  letterWheel: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split(''),
  colorWheel: ['Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple', 'Pink', 'Cyan'],
  foodWheel: ['Pizza', 'Sushi', 'Tacos', 'Pasta', 'Burger', 'Salad', 'Curry', 'Ramen'],
  moviePicker: ['Action', 'Comedy', 'Drama', 'Horror', 'Sci-Fi', 'Romance', 'Animation', 'Documentary'],
  gamePicker: ['Board Game', 'Video Game', 'Card Game', 'Outdoor Game', 'Party Game', 'Puzzle'],
  giveawayWheel: ['Prize 1', 'Prize 2', 'Prize 3', 'Prize 4', 'Prize 5', 'Grand Prize'],
  truthOrDare: ['Truth', 'Dare'],
  questionWheel: ['Who?', 'What?', 'When?', 'Where?', 'Why?', 'How?'],
  casinoWheel: ['Jackpot!', '$100', '$50', '$25', '$10', '$5', 'Try Again', 'Bonus Spin'],
  datePicker: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  countryWheel: ['USA', 'Japan', 'France', 'Brazil', 'Australia', 'Italy', 'Canada', 'Spain', 'Germany', 'UK'],
  drinkingGame: ['Take 1 sip', 'Take 2 sips', 'Waterfall', 'Give a drink', 'Skip turn', 'Truth or Drink', 'Categories'],
  musicWheel: ['Pop', 'Rock', 'Hip-Hop', 'Jazz', 'Classical', 'Electronic', 'Country', 'R&B'],
  sportsWheel: ['Soccer', 'Basketball', 'Tennis', 'Swimming', 'Running', 'Cycling', 'Golf', 'Baseball'],
  activityWheel: ['Watch a movie', 'Go for a walk', 'Read a book', 'Play games', 'Cook something', 'Exercise', 'Call a friend']
};

// Function to get translated presets
const getTranslatedPresets = (translations) => {
  const presets = translations?.wheelPresets;
  if (!presets) return DEFAULT_PRESETS;
  return presets;
};

// Variant-specific FAQ data
// (VARIANT_USE_CASES was removed 2026-09-26: it duplicated, in thinner form,
// what SEOContentSections already renders per variant from seo.<variant>.useCases)
const VARIANT_FAQS = {
  main: [
    { q: "Is this wheel spinner really random?", a: "Yes! We use the Web Crypto API (crypto.getRandomValues) for cryptographically secure randomness, superior to standard Math.random()." },
    { q: "Can I use this for giveaways?", a: "Absolutely! Our wheel is perfect for fair giveaways, raffles, and contests. The cryptographic randomness ensures unbiased results." },
    { q: "Does it work offline?", a: "Yes! wheel.expert is a Progressive Web App (PWA) that works offline after your first visit." },
    { q: "Is it free?", a: "100% free, no signup required, no ads, no limits." }
  ],
  yesNo: [
    { q: "How does the Yes or No wheel work?", a: "Simply spin the wheel and let fate decide! Our cryptographically secure algorithm ensures completely random, unbiased results." },
    { q: "Can I add 'Maybe' as an option?", a: "Yes! By default we include Yes, No, and Maybe. You can customize by removing Maybe or adding more options." },
    { q: "Is this good for decision making?", a: "Perfect for when you're stuck between two choices. Let the wheel decide and remove decision fatigue!" }
  ],
  namePicker: [
    { q: "How do I add names to the wheel?", a: "Type each name in the input field and press Enter or click Add. You can also paste multiple names at once." },
    { q: "Can I remove a winner after selection?", a: "Yes! Click 'Remove Winner' after each spin to eliminate that person from subsequent draws - perfect for raffles." },
    { q: "How many names can I add?", a: "There's no limit! Add as many names as you need for your classroom, team, or event." }
  ],
  teamGenerator: [
    { q: "How does team generation work?", a: "Add all participant names, then spin repeatedly. Remove each selected person to build fair, random teams." },
    { q: "Can I create multiple teams?", a: "Yes! Use the 'Remove Winner' feature to assign people to teams one by one, ensuring random distribution." }
  ],
  foodWheel: [
    { q: "What to eat for dinner?", a: "Spin the food wheel! We've pre-loaded popular options like Pizza, Sushi, Tacos, and more. Customize with your favorites." },
    { q: "Can I add restaurant names?", a: "Absolutely! Clear the presets and add your local restaurant names for where-to-eat decisions." }
  ],
  truthOrDare: [
    { q: "How to play Truth or Dare with the wheel?", a: "Each player spins to get either Truth or Dare. The group then asks a question or assigns a dare accordingly." },
    { q: "Can I customize the options?", a: "Yes! Add options like 'Double Dare', 'Skip', or custom challenges." }
  ],
  giveawayWheel: [
    { q: "Is this fair for giveaways?", a: "Yes! Our wheel uses crypto.getRandomValues() for true randomness. Each entry has an equal chance of winning." },
    { q: "Can I do multiple winners?", a: "Yes! Use 'Remove Winner' after each draw to select multiple unique winners without repeats." },
    { q: "Can I record the spin for proof?", a: "We recommend screen recording your spins for transparency in public giveaways." }
  ],
  casinoWheel: [
    { q: "Is this a real gambling wheel?", a: "No, this is for entertainment only. It's perfect for party games, mock casinos, or fun prize wheels." },
    { q: "Can I customize prizes?", a: "Yes! Edit the default options to add your own prizes, point values, or fun penalties." }
  ]
};

// Use Cases per variant
const WheelPage = ({ variant = 'main' }) => {
  const { t, isRTL, language, translations } = useLanguage();
  const { addToHistory } = useWheelHistory();
  const { celebrate, fireworks } = useConfetti();
  const wheelRef = useRef(null);
  const tournamentRef = useRef(null);
  const settingsRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [result, setResult] = useState(null);
  
  // Check if we're in embed mode (minimal UI)
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedMode = urlParams.get('embed') === 'true';
  const [showResult, setShowResult] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [spinResults, setSpinResults] = useState([]); // Leaderboard results
  const [isListHidden, setIsListHidden] = useState(false); // Surprise mode
  const [spinCount, setSpinCount] = useState(0); // Spin counter
  const [countdownActive, setCountdownActive] = useState(false); // Timer
  const [spinTrigger, setSpinTrigger] = useState(0); // Trigger spin after countdown
  const [showTournament, setShowTournament] = useState(false); // Tournament mode
  const [isWheelSpinning, setIsWheelSpinning] = useState(false); // Track if wheel is currently spinning
  
  // Get saved theme and sound style from localStorage
  const savedTheme = localStorage.getItem('wheelTheme') || 'default';
  const savedSoundStyle = localStorage.getItem('wheelSoundStyle') || 'classic';
  const savedVictorySound = localStorage.getItem('wheelVictorySound') || 'fanfare';
  
  const [settings, setSettings] = useState({
    sounds: true,
    soundStyle: savedSoundStyle,
    victorySound: savedVictorySound,
    spinDuration: 5000,
    confetti: true,
    removeWinnerAfterSpin: false,
    theme: savedTheme,
    useCountdown: false, // Timer setting
    countdownSeconds: 3
  });

  // Get current theme colors
  const themeColors = getThemeColors(settings.theme);
  const currentTheme = getTheme(settings.theme);

  // Save preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('wheelTheme', settings.theme);
  }, [settings.theme]);
  
  useEffect(() => {
    localStorage.setItem('wheelSoundStyle', settings.soundStyle);
  }, [settings.soundStyle]);
  
  useEffect(() => {
    localStorage.setItem('wheelVictorySound', settings.victorySound);
  }, [settings.victorySound]);

  // Initialize with preset entries for variant (and reload on language change)
  useEffect(() => {
    const defaultPresets = DEFAULT_PRESETS[variant] || [];
    if (defaultPresets.length > 0) {
      // Try to get translated presets from wheelPresets
      const translatedPresets = translations?.wheelPresets?.[variant];
      const optionsToUse = Array.isArray(translatedPresets) && translatedPresets.length > 0 
        ? translatedPresets 
        : defaultPresets;
      
      const entriesData = optionsToUse.map((entry, index) => ({
        id: `preset-${index}-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        text: entry,
        weight: 1,
        color: themeColors[index % themeColors.length]
      }));
      setEntries(entriesData);
    }
  }, [variant, language, translations]); // Re-run when variant, language, or translations changes

  // Update entry colors when theme changes
  useEffect(() => {
    if (entries.length > 0) {
      setEntries(prev => prev.map((entry, index) => ({
        ...entry,
        color: themeColors[index % themeColors.length]
      })));
    }
  }, [settings.theme]);

  // Handle entries change and assign colors
  const handleEntriesChange = useCallback((newEntries) => {
    const entriesWithColors = newEntries.map((entry, index) => ({
      ...entry,
      color: entry.color || themeColors[index % themeColors.length]
    }));
    setEntries(entriesWithColors);
  }, [themeColors]);

  // Filter only enabled entries for the wheel
  const enabledEntries = entries.filter(e => e.enabled !== false);

  // Handle spin end
  const handleSpinEnd = useCallback((winningEntry) => {
    setResult(winningEntry);
    setShowResult(true);
    addToHistory({ text: winningEntry.text, variant, color: winningEntry.color });
    
    // Increment spin counter
    setSpinCount(prev => prev + 1);
    
    // Add to leaderboard
    setSpinResults(prev => [...prev, winningEntry]);
    
    // Fire confetti if enabled
    if (settings.confetti) {
      celebrate();
    }
    
    // Announce winner for screen readers
    const announcer = document.getElementById('announcer');
    if (announcer) {
      announcer.textContent = `The winner is ${winningEntry.text}!`;
    }
    
    if (settings.removeWinnerAfterSpin && winningEntry && winningEntry.id) {
      // Remove winner immediately
      setEntries(prev => prev.filter(e => e.id !== winningEntry.id));
    }
  }, [addToHistory, variant, settings.removeWinnerAfterSpin, settings.confetti, celebrate]);

  // Clear leaderboard
  const clearLeaderboard = useCallback(() => {
    setSpinResults([]);
    setSpinCount(0);
  }, []);

  const handleCloseResult = useCallback(() => {
    setShowResult(false);
  }, []);

  const handleSpinAgain = useCallback(() => {
    setShowResult(false);
    setTimeout(() => {
      const spinButton = document.querySelector('[data-testid="spin-button"]');
      spinButton?.click();
    }, 300);
  }, []);

  const handleRemoveWinner = useCallback((winner) => {
    if (winner) {
      setEntries(prev => prev.filter(e => e.id !== winner.id));
    }
    setShowResult(false);
  }, []);

  // Toggle immersive fullscreen mode (in-app, not browser fullscreen)
  const toggleFullscreen = useCallback(() => {
    // Block fullscreen toggle while wheel is spinning
    if (isWheelSpinning) {
      return;
    }
    setIsFullscreen(prev => {
      // Clean up fullscreen confetti canvas when exiting
      if (prev) {
        const canvas = document.getElementById('fullscreen-confetti-canvas');
        if (canvas) {
          canvas.remove();
        }
      }
      return !prev;
    });
  }, [isWheelSpinning]);

  // Listen for ESC key to close fullscreen
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Block ESC exit while wheel is spinning
      if (e.key === 'Escape' && isFullscreen && !isWheelSpinning) {
        // Clean up fullscreen confetti canvas
        const canvas = document.getElementById('fullscreen-confetti-canvas');
        if (canvas) {
          canvas.remove();
        }
        setIsFullscreen(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, isWheelSpinning]);

  // Auto-scroll to tournament/settings when activated
  useEffect(() => {
    if (showTournament && tournamentRef.current) {
      setTimeout(() => {
        scrollToElement(tournamentRef.current, 120);
      }, 100);
    }
  }, [showTournament]);

  useEffect(() => {
    if (showSettings && settingsRef.current) {
      setTimeout(() => {
        scrollToElement(settingsRef.current, 120);
      }, 100);
    }
  }, [showSettings]);

  // Share wheel
  const handleShare = useCallback(async () => {
    const entriesParam = entries.map(e => encodeURIComponent(e.text)).join(',');
    const shareUrl = `${window.location.origin}${window.location.pathname}?entries=${entriesParam}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: t(`variants.${variant}.title`) || 'wheel.expert',
          text: t(`variants.${variant}.description`) || 'Spin the wheel!',
          url: shareUrl
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        // Could add a toast notification here
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    }
  }, [entries, variant, t]);

  // Export wheel as PNG image
  const handleExportPNG = useCallback(() => {
    const canvas = document.querySelector('[data-testid="wheel-canvas"]');
    if (!canvas) return;
    
    // Create a temporary canvas with white background
    const tempCanvas = document.createElement('canvas');
    const ctx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    
    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    
    // Draw the wheel
    ctx.drawImage(canvas, 0, 0);
    
    // Add watermark
    ctx.font = '14px Inter, Arial, sans-serif';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.textAlign = 'right';
    ctx.fillText('wheel.expert', tempCanvas.width - 10, tempCanvas.height - 10);
    
    // Download
    const link = document.createElement('a');
    // Convert camelCase to kebab-case for filename
    const filename = variant.replace(/([A-Z])/g, '-$1').toLowerCase();
    link.download = `wheel-${filename}-${Date.now()}.png`;
    link.href = tempCanvas.toDataURL('image/png');
    link.click();
  }, [variant]);

  // Export results as CSV
  const handleExportCSV = useCallback(() => {
    if (spinResults.length === 0) return;
    
    const csvContent = [
      'Rank,Winner,Color',
      ...spinResults.map((result, idx) => 
        `${idx + 1},"${result.text}","${result.color}"`
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `wheel-results-${Date.now()}.csv`;
    link.click();
  }, [spinResults]);

  // Load entries from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const entriesParam = params.get('entries');
    if (entriesParam) {
      const loadedEntries = entriesParam.split(',').map((text, index) => ({
        id: `url-${index}-${Date.now()}`,
        text: decodeURIComponent(text),
        weight: 1,
        color: themeColors[index % themeColors.length]
      }));
      setEntries(loadedEntries);
    }
  }, []);

  // Get localized variant info with correct slug from VARIANT_SLUGS
  const localizedSlug = getSlugForLanguage(variant, language);
  const variantInfo = {
    title: t(`variants.${variant}.title`) || 'Spin the Wheel',
    description: t(`variants.${variant}.description`) || t('site.description'),
    slug: localizedSlug || variant
  };

  // Get FAQs and UseCases from translations (with fallback to hardcoded)
  const translatedFaqs = t(`variants.${variant}.faqs`, { returnObjects: true });
  const mainFaqs = t(`variants.main.faqs`, { returnObjects: true });
  const faqs = Array.isArray(translatedFaqs) && translatedFaqs.length > 0
    ? translatedFaqs
    : (Array.isArray(mainFaqs) && mainFaqs.length > 0
        ? mainFaqs
        : (VARIANT_FAQS[variant] || VARIANT_FAQS.main));

  // TEMPORARILY DISABLED - SEO meta and schema (functions not imported)
  // const seoMeta = getSEOMeta(variant, language);
  // const enhancedSchemaData = generateEnhancedSchema(variant, language, {
  //   title: variantInfo.title,
  //   description: variantInfo.description,
  //   faqs: faqs
  // });
  
  // Embed Mode - Minimal UI with just the wheel
  if (isEmbedMode) {
    return (
      <div className="embed-container" style={{ padding: '1rem', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <SpinWheel
          entries={enabledEntries}
          onSpinEnd={handleSpinEnd}
          sounds={settings.sounds}
          soundStyle={settings.soundStyle}
          victorySound={settings.victorySound}
          spinDuration={settings.spinDuration}
        />
        
        {/* Result Modal in Embed */}
        {showResult && result && (
          <div className="modal-overlay">
            <div className="modal result-modal">
              <h2>🎉 Winner!</h2>
              <div className="result-display" style={{ backgroundColor: result.color }}>
                {result.text}
              </div>
              <button onClick={() => setShowResult(false)} className="btn-modal-action">
                Spin Again
              </button>
            </div>
          </div>
        )}
        
        <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#666' }}>
          Powered by <a href="https://wheel.expert" target="_blank" rel="noopener noreferrer" style={{ color: '#3B82F6' }}>wheel.expert</a>
        </p>
      </div>
    );
  }

  return (
    <>
      {/* TEMPORARILY DISABLED: customTitle, customDescription, customKeywords, enhancedSchema */}
      <SEOHead
        variant={variant}
        canonicalPath={
          variant === 'main'
            ? (language === 'en' ? '/' : `/${language}/`)
            : (language === 'en' ? `/${variantInfo.slug}/` : `/${language}/${variantInfo.slug}/`)
        }
        faqs={faqs}
      />
      
      <main id="main-content" className="main-content" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Hero Section */}
        <section className="hero">
          <h1 className="hero-title" data-testid="hero-title">
            {variantInfo.title}
          </h1>
          <p className="hero-description" data-testid="hero-description">
            {variantInfo.description}
          </p>
        </section>

        {/* Action Bar */}
        <div className="action-bar" data-testid="action-bar">
          <div className="action-bar-main">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`btn-action-bar ${showSettings ? 'active' : ''}`}
              aria-label={t('wheel.settings')}
              data-testid="settings-toggle"
            >
              <Settings size={20} />
              <span>{t('wheel.settings')}</span>
            </button>
            
            <button
              onClick={handleShare}
              className="btn-action-bar"
              aria-label={t('wheel.share')}
              data-testid="share-button"
            >
              <Share2 size={20} />
              <span>{t('wheel.share')}</span>
            </button>
            
            <button
              onClick={handleExportPNG}
              className="btn-action-bar"
              aria-label={t('wheel.export')}
              data-testid="export-png-button"
            >
              <Download size={20} />
              <span>{t('wheel.export')}</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="btn-action-bar"
              aria-label={t('wheel.fullscreen')}
              data-testid="fullscreen-toggle"
            >
              <Maximize2 size={20} />
              <span>{t('wheel.fullscreen')}</span>
            </button>
          </div>
          
          <div className="action-bar-tools">
            {/* Save/Load Wheel */}
            <SaveLoadWheel 
              entries={entries}
              onLoad={(loadedEntries) => {
                const entriesWithColors = loadedEntries.map((e, i) => ({
                  ...e,
                  id: `loaded-${i}-${Date.now()}`,
                  color: themeColors[i % themeColors.length]
                }));
                setEntries(entriesWithColors);
              }}
              variant={variant}
            />
            
            {/* QR Code Share */}
            <QRCodeShare entries={entries} variant={variant} />
            
            {/* Statistics - only show if we have spins */}
            {spinResults.length > 0 && (
              <SpinStatistics results={spinResults} entries={entries} />
            )}
            
            {/* Embed Code */}
            <EmbedCode entries={entries} variant={variant} />
            
            {/* Team Mode */}
            <TeamMode entries={entries} onEntriesChange={handleEntriesChange} />
            
            {/* Tournament Mode */}
            <button
              onClick={() => setShowTournament(!showTournament)}
              className={`btn-action-bar ${showTournament ? 'active' : ''}`}
              aria-label={t('wheel.tournamentMode')}
              data-testid="tournament-button"
              disabled={entries.filter(e => e.enabled !== false).length < 2}
            >
              <Trophy size={20} />
              <span>{t('wheel.tournament')}</span>
            </button>
            
            {/* History */}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`btn-action-bar ${showHistory ? 'active' : ''}`}
              aria-label={t('wheel.history')}
              data-testid="history-toggle"
            >
              <History size={20} />
              <span>{t('wheel.history')}</span>
            </button>
          </div>
        </div>

        {/* Panels */}
        {showSettings && (
          <div ref={settingsRef}>
            <SettingsPanel 
              settings={settings} 
              onSettingsChange={setSettings}
              onClose={() => setShowSettings(false)}
            />
          </div>
        )}
        
        {showHistory && (
          <HistoryPanel onClose={() => setShowHistory(false)} />
        )}

        {/* Wheel Section - hide when fullscreen to avoid dual canvas issues */}
        {!isFullscreen && (
        <section className="wheel-section" data-testid="wheel-section">
          <div className="wheel-with-counter">
            <SpinWheel
              key="normal-wheel"
              entries={enabledEntries}
              onSpinEnd={handleSpinEnd}
              sounds={settings.sounds}
              soundStyle={settings.soundStyle}
              victorySound={settings.victorySound}
              spinDuration={settings.spinDuration}
              triggerSpin={spinTrigger}
              isSpinning={isWheelSpinning}
              onSpinningChange={setIsWheelSpinning}
              onBeforeSpin={() => {
                // If countdown is enabled and not already active, show countdown first
                if (settings.useCountdown && !countdownActive) {
                  setCountdownActive(true);
                  return false; // Prevent immediate spin, countdown will trigger it
                }
                return true; // Allow spin
              }}
            />
            
            {/* Spin Counter - under the wheel */}
            {spinCount > 0 && (
              <div className="spin-counter" data-testid="spin-counter">
                <span className="counter-label">Spins:</span>
                <span className="counter-value">{spinCount}</span>
              </div>
            )}
          </div>
          
          <EntryList
            entries={entries}
            onEntriesChange={handleEntriesChange}
            variant={variant}
            isHidden={isListHidden}
            onToggleHidden={() => setIsListHidden(!isListHidden)}
          />
          
          {/* Leaderboard - Results Order */}
          <Leaderboard 
            results={spinResults} 
            onClear={clearLeaderboard}
            onExportCSV={handleExportCSV}
          />
          
          {/* Tournament Mode */}
          {showTournament && (
            <div ref={tournamentRef}>
              <TournamentMode 
                entries={entries}
                onClose={() => setShowTournament(false)}
              />
            </div>
          )}
        </section>
        )}

        {/* Countdown Overlay - only show when NOT in fullscreen */}
        {countdownActive && !isFullscreen && (
          <CountdownOverlay 
            seconds={settings.countdownSeconds || 3}
            soundEnabled={settings.sounds}
            onComplete={() => {
              setCountdownActive(false);
              // Trigger spin after countdown by incrementing spinTrigger
              setSpinTrigger(prev => prev + 1);
            }}
          />
        )}

        {/* Enhanced SEO Content Sections - Only show on variant pages */}
        {variant !== 'main' && (
          <>
            {/* Re-enabled after white-screen fix (defensive null/array guards in SEOContentSections) */}
            <SEOContentSections variant={variant} />

            {/* TEMPORARILY DISABLED - Debugging production white screen */}
            {/* <ViralShareButtons
              variant={variant}
              language={language}
              currentState={{
                entries: entries.map(e => e.text),
                theme: settings.theme,
                title: variantInfo.title
              }}
            /> */}

            {/* Randomness/trust explanation lives once on the homepage (#how-it-works) —
                it's the same mechanism for every variant, so repeating the full
                explanation on all 19 variant pages was duplicate content. Link instead. */}
            <p className="algo-trust-link">
              <Shield size={16} /> {t('seo.common.secure')} —{' '}
              <a href={`${language === 'en' ? '/' : `/${language}/`}#how-it-works`}>
                {t('seo.algorithm.title')}
              </a>
            </p>

            {/* FAQ Section */}
            {/* Removed duplicate FAQPage schema - already in SEOHead */}
            <section className="seo-section faq-section">
              <h2>{t('seo.faq.title')}</h2>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className={`faq-item ${expandedFaq === index ? 'expanded' : ''}`}
                  >
                    <button
                      className="faq-question"
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      aria-expanded={expandedFaq === index}
                    >
                      <span>{faq.q}</span>
                      {expandedFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    <div
                      className="faq-answer"
                      hidden={expandedFaq !== index}
                    >
                      <p>{faq.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}

        {/* Result Modal */}
        <ResultModal
          result={result}
          isOpen={showResult}
          onClose={handleCloseResult}
          onSpinAgain={handleSpinAgain}
          onRemoveWinner={handleRemoveWinner}
          showRemoveOption={!settings.removeWinnerAfterSpin && entries.length > 1}
        />

        {/* Screen reader announcer */}
        <div id="announcer" role="status" aria-live="polite" aria-atomic="true" />
      </main>

      {/* Immersive Fullscreen Mode */}
      {isFullscreen && (
        <div className="fullscreen-mode" data-testid="fullscreen-mode">
          <div className="fullscreen-header">
            <button
              onClick={toggleFullscreen}
              className="fullscreen-close"
              aria-label="Exit fullscreen"
              data-testid="fullscreen-close"
            >
              <X size={24} />
            </button>
          </div>
          
          <div className="fullscreen-content">
            <div className="fullscreen-wheel">
              <SpinWheel
                key="fullscreen-wheel"
                entries={enabledEntries}
                onSpinEnd={handleSpinEnd}
                sounds={settings.sounds}
                soundStyle={settings.soundStyle}
                victorySound={settings.victorySound}
                spinDuration={settings.spinDuration}
                triggerSpin={spinTrigger}
                isSpinning={isWheelSpinning}
                onSpinningChange={setIsWheelSpinning}
                onBeforeSpin={() => {
                  // If countdown is enabled and not already active, show countdown first
                  if (settings.useCountdown && !countdownActive) {
                    setCountdownActive(true);
                    return false; // Prevent immediate spin, countdown will trigger it
                  }
                  return true; // Allow spin
                }}
              />
            </div>
          </div>
          
          {/* Countdown Overlay in Fullscreen */}
          {countdownActive && (
            <CountdownOverlay 
              seconds={settings.countdownSeconds || 3}
              soundEnabled={settings.sounds}
              onComplete={() => {
                setCountdownActive(false);
                setSpinTrigger(prev => prev + 1);
              }}
            />
          )}
          
          {/* Fullscreen Leaderboard */}
          {spinResults.length > 0 && (
            <div className="fullscreen-leaderboard">
              <div className="fullscreen-leaderboard-list">
                {spinResults.map((res, idx) => (
                  <div key={`fs-${res.id}-${idx}`} className="fullscreen-leaderboard-item">
                    <span className="rank">#{idx + 1}</span>
                    <span 
                      className="leaderboard-color" 
                      style={{ backgroundColor: res.color }}
                    />
                    <span>{res.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Result Modal in Fullscreen - Same as normal modal */}
          <ResultModal
            result={result}
            isOpen={showResult}
            onClose={handleCloseResult}
            onSpinAgain={handleSpinAgain}
            onRemoveWinner={handleRemoveWinner}
            showRemoveOption={!settings.removeWinnerAfterSpin && entries.length > 1}
          />
        </div>
      )}
    </>
  );
};

export default WheelPage;
